import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Bell, User, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import SSOLoginButton from "./SSOLoginButton";

const NavigationBar: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
const handleBellClick = () => {
  const forumUrl = process.env.REACT_APP_DISCOURSE_FORUM_URL || 'https://forum.commandlegacy.us';
  window.open(`${forumUrl}/my/notifications`, '_blank');
};
  return (
    <nav className="flex items-center justify-between px-4 md:px-6 py-4 bg-slate-900 relative w-full border-b border-slate-700">
      {/* Hamburger Menu - Only show when authenticated */}
      {isAuthenticated && (
        <button
          className="text-command-light-blue hover:bg-slate-800 p-2 rounded-md md:hidden"
          onClick={toggleMobileMenu}
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-command-light-blue" />
          ) : (
            <Menu className="h-6 w-6 text-command-light-blue" />
          )}
        </button>
      )}

      {/* Logo and Title */}
      <div className="flex flex-col items-center">
        <Link to="/" className="hover:opacity-80 transition-opacity">
          <h1 className="text-xl font-bold text-command-light-blue">
            Command Legacy
          </h1>
          <p className="text-sm text-gray-300 opacity-70 text-center">
            Police Leadership Platform
          </p>
        </Link>
      </div>

      {/* Navigation Links - Only show when authenticated */}
      {isAuthenticated && (
        <div className="hidden md:flex items-center space-x-6">
          <Link
            to="/"
            className={`px-3 py-2 rounded-md transition-colors ${
              isActive("/")
                ? "bg-command-light-blue text-slate-900 font-medium"
                : "text-command-light-blue hover:bg-slate-800"
            }`}
          >
            Home
          </Link>
          <Link
            to="/legal-search"
            className={`px-3 py-2 rounded-md transition-colors ${
              isActive("/legal-search")
                ? "bg-command-light-blue text-slate-900 font-medium"
                : "text-command-light-blue hover:bg-slate-800"
            }`}
          >
            Legal Search
          </Link>
          <Link
            to="/discussion-forum"
            className={`px-3 py-2 rounded-md transition-colors ${
              isActive("/discussion-forum")
                ? "bg-command-light-blue text-slate-900 font-medium"
                : "text-command-light-blue hover:bg-slate-800"
            }`}
          >
            Forum
          </Link>
          <Link
            to="/activity"
            className={`px-3 py-2 rounded-md transition-colors ${
              isActive("/activity")
                ? "bg-command-light-blue text-slate-900 font-medium"
                : "text-command-light-blue hover:bg-slate-800"
            }`}
          >
            Activity
          </Link>
        </div>
      )}

      {/* Right Icons - Show SSO button when not authenticated, user info when authenticated */}
      <div className="flex items-center space-x-2">
      {isAuthenticated && (
  <button 
    className="text-command-light-blue hover:bg-slate-800 p-2 rounded-md"
    onClick={handleBellClick}
    title="View Notifications"
  >
    <Bell className="h-6 w-6 text-command-light-blue" />
  </button>
)}

        
        {isAuthenticated && user ? (
          <div className="flex items-center space-x-2">
            {user.avatar_url && (
              <img 
                src={user.avatar_url} 
                alt={user.name || user.username}
                className="w-8 h-8 rounded-full border-2 border-command-light-blue"
              />
            )}
            <span className="text-command-light-blue text-sm hidden md:block">
              {user.name || user.username}
            </span>
            <a 
              href={(process.env.REACT_APP_DISCOURSE_FORUM_URL || 'https://forum.commandlegacy.us') + '/my/preferences/account'}
              className="text-command-light-blue hover:bg-slate-800 p-2 rounded-md"
              title="Profile / Settings"
            >
              <User className="h-6 w-6 text-command-light-blue" />
            </a>
          </div>
        ) : (
          <SSOLoginButton
            onAuthSuccess={(user) => {
              console.log('Authentication successful:', user);
            }}
            onAuthError={(error) => {
              console.error('Authentication failed:', error);
            }}
            variant="compact"
          >
            Login via Forum
          </SSOLoginButton>
        )}
      </div>

      {/* Mobile Menu - Only show when authenticated */}
      {isMobileMenuOpen && isAuthenticated && (
        <div className="absolute top-full left-0 right-0 bg-slate-800 border-t border-slate-700 md:hidden z-40">
          <div className="px-6 py-4 space-y-2">
            <Link
              to="/"
              className={`block px-3 py-2 rounded-md transition-colors ${
                isActive("/")
                  ? "bg-command-light-blue text-slate-900 font-medium"
                  : "text-command-light-blue hover:bg-slate-700"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/legal-search"
              className={`block px-3 py-2 rounded-md transition-colors ${
                isActive("/legal-search")
                  ? "bg-command-light-blue text-slate-900 font-medium"
                  : "text-command-light-blue hover:bg-slate-700"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Legal Search
            </Link>
            <Link
              to="/discussion-forum"
              className={`block px-3 py-2 rounded-md transition-colors ${
                isActive("/discussion-forum")
                  ? "bg-command-light-blue text-slate-900 font-medium"
                  : "text-command-light-blue hover:bg-slate-700"
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Forum
            </Link>
            <Link
              to="/activity"
              className={`block px-3 py-2 rounded-md transition-colors ${
                isActive("/activity")
                  ? "bg-command-light-blue text-slate-900 font-medium"
                  : "text-command-light-blue hover:bg-slate-800"
              }`}
            >
              Activity
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;
